import * as XLSX from 'xlsx';

/**
 * Utility for exporting data to CSV (SAP) and XLSX (Excel) formats.
 */

/**
 * Formats a date string or object to SAP's standard YYYYMMDD format.
 */
export const formatSAPDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

/**
 * Formats a date string to a human-readable format for Excel.
 */
export const formatReadableDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  });
};

/**
 * Downloads a CSV file (optimized for SAP technical imports).
 */
export const downloadCSV = (headers, rows, filename, useBOM = false) => {
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(val => {
      const stringVal = String(val);
      if (stringVal.includes(',') || stringVal.includes('"') || stringVal.includes('\n')) {
        return `"${stringVal.replace(/"/g, '""')}"`;
      }
      return stringVal;
    }).join(','))
  ].join('\n');

  const content = useBOM ? '\uFEFF' + csvContent : csvContent;
  
  // Clean filename and ensure extension
  let cleanName = filename.replace(/[<>:"/\\|?*]/g, '').trim();
  const safeFilename = cleanName.toLowerCase().endsWith('.csv') ? cleanName : `${cleanName}.csv`;

  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', safeFilename); // Use setAttribute for better compatibility
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
};

/**
 * Downloads an XLSX file with table formatting (Optimized for Excel daily work).
 */
export const downloadXLSX = (headers, rows, filename, sheetName = 'Export') => {
  const data = [headers, ...rows];
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(data);

  // Auto-width for columns
  const colWidths = headers.map((h, i) => {
    let max = h.length;
    rows.forEach(row => {
      const val = row[i] ? String(row[i]).length : 0;
      if (val > max) max = val;
    });
    return { wch: max + 2 };
  });
  worksheet['!cols'] = colWidths;

  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.substring(0, 31)); // sheet names limited to 31 chars
  
  // Clean filename and ensure extension
  let cleanName = filename.replace(/[<>:"/\\|?*]/g, '').trim();
  const safeFilename = cleanName.toLowerCase().endsWith('.xlsx') ? cleanName : `${cleanName}.xlsx`;
  
  // Manually trigger download to ensure extension is respected
  const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', safeFilename);
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
};

/**
 * Technical SAP Header Mappings for CATS (Cross-Application Time Sheet)
 */
export const CATS_HEADERS = {
  PERNR: 'PersonnelNumber',
  WORKDATE: 'WorkDate',
  LSTAR: 'ActivityType',
  SKOSTL: 'SenderCostCenter',
  SE_WBS_ELEMENT: 'ReceiverWBS',
  CATSAMOUNT: 'Hours'
};
